import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBeforeunload } from 'react-beforeunload';
import { completeVideoUpload } from '../services/admin/admin-video.service';
import { uploadResumable } from '../services/gcs-resumable-upload';
import { getAxiosErrorMessage } from '../common/utils/helpers';
import { getHttpStatus } from '../common/utils/http-status';
import {
  applyVideoUpdate,
  invalidateVideoQueries,
} from '../common/utils/video-query-cache';
import {
  isUploadPending,
  uploadsToStart,
  videoUploadQueueReducer,
} from './video-upload-queue';
import {
  NewVideoUpload,
  VideoUploadContextValue,
  VideoUploadItem,
} from './video-upload.types';
import OvVideoUploadTray from '../components/UI/organisms/OvVideoUploadTray';

export const VideoUploadContext = createContext<VideoUploadContextValue | null>(
  null
);

interface VideoUploadProviderProps {
  children: ReactNode;
}

export const VideoUploadProvider: FC<VideoUploadProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const [uploads, dispatch] = useReducer(videoUploadQueueReducer, []);
  const controllers = useRef(new Map<string, AbortController>());
  const [isTrayExpanded, setIsTrayExpanded] = useState(true);
  const [trayFocusRequest, setTrayFocusRequest] = useState(0);

  const run = useCallback(
    async ({
      videoId,
      uploadUrl,
      file,
      contentType,
      resume,
    }: VideoUploadItem) => {
      const controller = new AbortController();
      controllers.current.set(videoId, controller);

      try {
        await uploadResumable(uploadUrl, file, {
          contentType,
          resume,
          signal: controller.signal,
          onProgress: (uploadedBytes) =>
            dispatch({ type: 'progressed', videoId, uploadedBytes }),
        });

        if (controller.signal.aborted) {
          return;
        }

        dispatch({ type: 'completing', videoId });
        const completed = await completeVideoUpload(videoId);

        if (controller.signal.aborted) {
          return;
        }

        applyVideoUpdate(queryClient, completed);
        dispatch({ type: 'finished', video: completed });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        if (getHttpStatus(error) === 409) {
          dispatch({ type: 'removed', videoId });
          invalidateVideoQueries(queryClient);

          return;
        }

        dispatch({
          type: 'failed',
          videoId,
          error: getAxiosErrorMessage(error),
        });
      } finally {
        controllers.current.delete(videoId);
      }
    },
    [queryClient]
  );

  useEffect(() => {
    uploadsToStart(uploads).forEach((item) => {
      dispatch({ type: 'started', videoId: item.videoId });
      run(item);
    });
  }, [uploads, run]);

  useEffect(() => {
    const activeControllers = controllers.current;

    return () => activeControllers.forEach((controller) => controller.abort());
  }, []);

  const hasPendingUploads = uploads.some(isUploadPending);

  useBeforeunload(
    hasPendingUploads ? (event) => event.preventDefault() : undefined
  );

  const enqueue = useCallback(
    (newUploads: NewVideoUpload[]) =>
      dispatch({ type: 'enqueued', uploads: newUploads }),
    []
  );

  const retry = useCallback(
    (videoId: string) => dispatch({ type: 'retried', videoId }),
    []
  );

  const cancel = useCallback((videoId: string) => {
    controllers.current.get(videoId)?.abort();
    dispatch({ type: 'cancelled', videoId });
  }, []);

  const dismiss = useCallback(
    (videoId: string) => dispatch({ type: 'removed', videoId }),
    []
  );

  const clearFinished = useCallback(
    () => dispatch({ type: 'finishedCleared' }),
    []
  );

  const expandTray = useCallback(() => setIsTrayExpanded(true), []);

  const showTray = useCallback(() => {
    setIsTrayExpanded(true);
    setTrayFocusRequest((request) => request + 1);
  }, []);

  const value = useMemo(
    () => ({
      uploads,
      enqueue,
      retry,
      cancel,
      dismiss,
      clearFinished,
      expandTray,
      showTray,
    }),
    [
      uploads,
      enqueue,
      retry,
      cancel,
      dismiss,
      clearFinished,
      expandTray,
      showTray,
    ]
  );

  return (
    <VideoUploadContext.Provider value={value}>
      {children}
      <OvVideoUploadTray
        uploads={uploads}
        isExpanded={isTrayExpanded}
        focusRequest={trayFocusRequest}
        onToggle={() => setIsTrayExpanded((expanded) => !expanded)}
      />
    </VideoUploadContext.Provider>
  );
};
